/**
 * Navigation components.
 * @module components/theme/Navigation/Navigation
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import {
  Header,
  HeaderContent,
  HeaderToggler,
  Nav,
  NavItem,
  NavLink,
} from 'design-react-kit/dist/design-react-kit';
import { Collapse } from '@italia/components';
import {
  MegaMenu,
  MenuSecondary,
  Logo,
  Icon,
  SocialHeader,
  BrandText,
} from '@italia/components/ItaliaTheme';

import {
  getDropdownMenuNavitems,
  getItemsByPath,
} from 'volto-dropdownmenu';

const renderSubsiteSlimHeaderMenu = () => {
  return (
    <div id="it-subsite-slim-header">
      <Nav vertical={false} className="d-md-flex">
        <NavItem tag="li">
          <NavLink to="/cerca-nei-cataloghi" tag={Link}>
            <span>Catalogo on line</span>
          </NavLink>
        </NavItem>
        <NavItem tag="li">
          <NavLink
            to="/cosa-puoi-fare/altre-attivita/calendario-eventi"
            tag={Link}
          >
            <span>Eventi</span>
          </NavLink>
        </NavItem>
        <NavItem tag="li">
          <NavLink to="/chiedi-al-bibliotecario" tag={Link}>
            <span>Chiedi al bibliotecario</span>
          </NavLink>
        </NavItem>
        <NavItem tag="li">
          <NavLink to="/avvisi" tag={Link}>
            <span>Avvisi</span>
          </NavLink>
        </NavItem>
        <NavItem tag="li">
          <NavLink to="/orari" tag={Link}>
            <span>Orari</span>
          </NavLink>
        </NavItem>
      </Nav>
    </div>
  );
};

const Navigation = ({ pathname }) => {
  const [collapseOpen, setCollapseOpen] = useState(false);
  const dispatch = useDispatch();

  const items = useSelector((state) => state.dropdownMenuNavItems?.result);
  useEffect(() => {
    dispatch(getDropdownMenuNavitems());
  }, [dispatch]);

  const menu = getItemsByPath(items, pathname);
  const getAnchorTarget = (nodeElement) => {
    if (nodeElement.nodeName === 'A') {
      return nodeElement;
    } else if (nodeElement.parentElement?.nodeName === 'A') {
      return nodeElement.parentElement;
    } else {
      return null;
    }
  };

  useEffect(() => {
    const blocksClickListener = (e) => {
      const menuLinks = [
        ...(document?.querySelectorAll(
          '.menu-wrapper a:not([aria-haspopup])',
        ) ?? []),
      ];

      if (
        menuLinks?.length === 0 ||
        menuLinks?.indexOf(getAnchorTarget(e.target)) < 0
      ) {
        return;
      }

      setCollapseOpen(false);
    };

    document.body.addEventListener('click', blocksClickListener);

    return () =>
      document.body.removeEventListener('click', blocksClickListener);
  }, []);

  return (
    <Header theme="" type="navbar">
      {menu?.length > 0 ? (
        <HeaderContent expand="lg" megamenu id="navigation">
          <HeaderToggler
            aria-controls="#it-navigation-collapse"
            aria-expanded={collapseOpen}
            aria-label="Toggle navigation"
            onClick={() => setCollapseOpen(!collapseOpen)}
          >
            <Icon icon="it-burger" />
          </HeaderToggler>
          <Collapse
            header
            isOpen={collapseOpen}
            navbar
            onOverlayClick={() => setCollapseOpen(!collapseOpen)}
            id="it-navigation-collapse"
          >
            <div className="menu-wrapper">
              <div className="it-brand-wrapper" role="navigation">
                <Link to={'/'}>
                  <Logo />
                  <BrandText mobile={true} subsite={null} />
                </Link>
              </div>
              <Nav navbar>
                {menu
                  ?.filter((item) => item.visible)
                  ?.map((item, index) => (
                    <MegaMenu
                      item={item}
                      pathname={pathname}
                      key={index + 'mm'}
                    />
                  ))}
              </Nav>
              <MenuSecondary pathname={pathname} />
              <SocialHeader />

              {renderSubsiteSlimHeaderMenu()}
            </div>
          </Collapse>
        </HeaderContent>
      ) : null}
    </Header>
  );
};

Navigation.propTypes = {
  pathname: PropTypes.string.isRequired,
};

export default Navigation;
